import React from 'react';

import { Col, Container, Row, Spinner, Table } from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';

import { TablePagination } from '../../../components';

import './styles.scoped.css';

const columnClassName = 'align-middle text-nowrap';

// Numeric-aware sort so react-table orders hours columns by value whether the proc
// returns a string or a number.
const numericSort = (rowA, rowB, columnId) => Number(rowA.values[columnId]) - Number(rowB.values[columnId]);

// Column order/labels per the CP-801 business spec (Timesheet worksheet).
// Accessors must match the exact aliased column names returned by
// tb_sp_cl_proscore_timesheet (see CP-800 / cp-appsync-long-running-report resolver →
// centerline-driver-qualifications-ultrastaff-sql). Like sp_cl_cp_bi_401k_sa, the
// ProScore procs alias columns with spaces, so accessors use the display labels.
// Verify these against the proc output before release.
export const prevailingWageColumns = [
  { Header: 'Week Ending Date', accessor: 'Week Ending Date', className: columnClassName },
  { Header: 'Last Name', accessor: 'Last Name', className: columnClassName },
  { Header: 'First Name', accessor: 'First Name', className: columnClassName },
  { Header: 'Employee Internal ID', accessor: 'Employee Internal Id', className: columnClassName },
  { Header: 'Labor Classification', accessor: 'Labor Classification', className: columnClassName },
  { Header: 'Worked Date', accessor: 'Worked Date', className: columnClassName },
  {
    Header: 'Total Standard Hours',
    accessor: 'Total Standard Hours',
    className: columnClassName,
    sortType: numericSort,
  },
  {
    Header: 'Total Overtime Hours',
    accessor: 'Total Over Time Hours',
    className: columnClassName,
    sortType: numericSort,
  },
  { Header: 'Amendment', accessor: 'Amendment', className: columnClassName },
  { Header: 'Supplement', accessor: 'Supplement', className: columnClassName },
];

function getSortedIcon(isSorted, isSortedDesc) {
  let iconProp = <FaSort />;
  if (isSorted) {
    iconProp = isSortedDesc ? <FaSortDown /> : <FaSortUp />;
  }
  return iconProp;
}

const PrevailingWageTable = ({ data, loading }) => {
  const tableData = Array.isArray(data) ? data : [];
  const tableInstance = useTable(
    {
      columns: prevailingWageColumns,
      data: tableData,
      initialState: {
        pageSize: 200,
        sortBy: [{ id: 'Week Ending Date', desc: false }],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const tableHeaders = headerGroups.map(headerGroup => {
    const headers = headerGroup.headers.map(column => (
      <th {...column.getHeaderProps(column.getSortByToggleProps())}>
        {column.render('Header')}
        <span>{column.canSort ? getSortedIcon(column.isSorted, column.isSortedDesc) : null}</span>
      </th>
    ));
    return <tr {...headerGroup.getHeaderGroupProps()}>{headers}</tr>;
  });

  const tableRows = page.map(row => {
    prepareRow(row);
    const cells = row.cells.map(cell => (
      <td
        {...cell.getCellProps({
          className: cell.column.className,
          style: cell.column.style,
        })}>
        {cell.render('Cell')}
      </td>
    ));

    return <tr {...row.getRowProps()}>{cells}</tr>;
  });

  return (
    <Container fluid>
      <Row className="align-items-center">
        <Col className="d-flex justify-content-center justify-content-md-end">
          <TablePagination tableInstance={tableInstance} />
        </Col>
      </Row>
      <Row>
        <Col className="border border-light rounded shadow-sm">
          <Table hover responsive borderless {...getTableProps()}>
            <thead className="text-nowrap">{tableHeaders}</thead>
            <tbody {...getTableBodyProps()}>{tableRows}</tbody>
          </Table>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
            {loading && <Spinner animation="border" />}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PrevailingWageTable;
